import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useAuth } from '../../context/AuthContext';
import storyBaseService from '../../service/story-base.service';
import { useNavigate } from 'react-router-dom';
import { useHomeTemplates } from '../../context/HomeTemplatesContext';

const VideoTemplate: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { items, loading, error, page, hasMore, scrollY, search, setState } = useHomeTemplates();
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);
  const [searchInput, setSearchInput] = React.useState<string>(search || '');

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!user?.id) return;
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const data = await storyBaseService.getStoryBasesByMember(user.id, {
          page: 1,
          limit: 20,
          search: search || undefined,
        });
        if (!alive) return;
        setState((prev) => ({
          ...prev,
          items: data || [],
          page: 1,
          hasMore: (data || []).length === 20,
          loading: false,
        }));
      } catch (e: any) {
        if (!alive) return;
        setState((prev) => ({
          ...prev,
          error: e?.response?.data?.message || e?.message || 'Failed to load templates',
          loading: false,
        }));
      }
    };
    if (!items && user?.id) {
      run();
    }
    return () => {
      alive = false;
    };
  }, [user?.id, items, search, setState]);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && scrollY > 0) {
      window.scrollTo(0, scrollY);
    }
  }, [scrollY]);

  const handleLoadMore = async () => {
    if (!user?.id || loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const data = await storyBaseService.getStoryBasesByMember(user.id, {
        page: nextPage,
        limit: 20,
        search: search || undefined,
      });
      const list = data || [];
      setState((prev) => ({
        ...prev,
        items: [...(prev.items || []), ...list],
        page: nextPage,
        hasMore: list.length === 20,
      }));
    } catch (e: any) {
      setState((prev) => ({
        ...prev,
        error: e?.response?.data?.message || e?.message || 'Failed to load templates',
      }));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Video Templates</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search by title"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            setState((prev) => ({
              ...prev,
              search: searchInput.trim(),
              items: null,
              page: 1,
              hasMore: true,
            }));
          }}
        >
          Ara
        </Button>
      </Box>
      {loading && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
            width: '100%',
            overflowX: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <Box key={i}>
              <Card>
                <CardContent>
                  <Skeleton width="70%" />
                  <Skeleton width="40%" />
                  <Skeleton width="30%" />
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}
      {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && !error && items && items.length === 0 && (
        <Alert severity="info">Henüz şablonun yok.</Alert>
      )}
      {!loading && !error && items && items.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
            width: '100%',
            overflowX: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {items.map((t) => {
            const defaultImg =
              t.target === 'short'
                ? 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRFBr8U-wvWLbkURPxHOF0zOmkrYNbOeSJXw_GEMckj8PHaJOthImF6WIbVyb_L_wfKUYQ&usqp=CAU'
                : t.target === 'youtube'
                ? 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcThQIHDSLUUjj3k8WNaNlA7n1VMd5Enba8mFg&s'
                : 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTtvI3Phe62PcK3oeViRAxXNIWoEedgAS1rFg&s';
            // const latestRunImage = (t.runs || [])
            //   .slice()
            //   .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
            //   .map((r) => (r.baseImageUrl || '').trim())
            //   .filter(Boolean)
            //   .pop();
            const cardImg = defaultImg; // baseImageUrl yerine her zaman default görsel kullan
            return (
            <Box key={t.id} sx={{ minWidth: 0 }}>
              <Card>
                <CardActionArea
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      const currentY = window.scrollY || 0;
                      setState((prev) => ({ ...prev, scrollY: currentY }));
                    }
                    navigate(`/templates/${t.id}`);
                  }}
                >
                  <CardMedia
                    component="img"
                    image={cardImg}
                    alt={t.target}
                    sx={{ height: 160, objectFit: 'cover' }}
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      if (img && img.src !== defaultImg) {
                        img.src = defaultImg;
                      }
                    }}
                  />
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      title={t.title}
                      sx={{
                        wordBreak: 'break-word',
                      }}
                    >
                      {t.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                      {t.language?.toUpperCase()} • {t.target} • v{t.version}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {new Date(t.updatedAt || t.createdAt).toLocaleString()}
                    </Typography>
                    {typeof t.estimatedDurationMs === 'number' && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Estimated Duration: {t.estimatedDurationMs} ms • {Math.round((t.estimatedDurationMs || 0) / 1000)} sn
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Box>
            );
          })}
        </Box>
      )}
      {!loading && !error && items && items.length > 0 && hasMore && (
        <Box sx={{ mt: 2 }}>
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', cursor: 'pointer' }}
          >
            {loadingMore ? 'Yükleniyor...' : 'Sonrakini getir'}
          </button>
        </Box>
      )}
    </Box>
  );
};

export default VideoTemplate;
